import React, { useEffect, useState } from "react";
import axios from "axios";
import { Panel } from "@/components/common/Panel";
import { formatDate, fromNow } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AlertTriangle, ArrowRight, Gavel, ScrollText, ListTodo, FileText, GitBranch, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TONE = { HEARING_SOON: "text-copper", DEADLINE_SOON: "text-danger", NEW_ORDER: "text-info", FACT_CONFLICT: "text-danger", MISSING_EVIDENCE: "text-warning", CITATION_ALERT: "text-warning", INSTRUCTIONS_PENDING: "text-warning", REVIEW_PENDING: "text-info", TASK_OVERDUE: "text-danger", LEGAL_HOLD: "text-danger", INTEL_PENDING: "text-copper" };

export function useSnapshot(matterId) {
  const [snap, setSnap] = useState(null);
  const reload = () => axios.get(`${API}/matters/${matterId}/snapshot`).then((r) => setSnap(r.data)).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [matterId]);
  return [snap, reload];
}

export function AttentionBar({ snap, onOpen }) {
  if (!snap?.attention?.length) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-2" data-testid="attention-bar">
      {snap.attention.map((a, i) => (
        <button key={`${a.kind}-${i}`} onClick={() => onOpen(a.tab)} data-testid={`attention-${a.kind}`}
          className={cn("flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1 text-xs font-medium transition-colors hover:border-copper/50", TONE[a.kind])}>
          <AlertTriangle className="h-3 w-3" /> {a.label}
        </button>
      ))}
    </div>
  );
}

export function SnapshotCommandCentre({ snap, onOpen, children }) {
  if (!snap) return <Panel className="p-6 text-sm text-ink-muted">Loading command centre…</Panel>;
  const cards = [
    { icon: Gavel, k: "Current Proceeding", v: snap.primaryProceeding?.caseNumber || snap.matter.caseNumber, s: `${snap.proceedingCount} proceeding(s)`, tab: "Proceedings" },
    { icon: Gavel, k: "Next Hearing", v: snap.nextHearing ? formatDate(snap.nextHearing.date) : "None scheduled", s: snap.nextHearing?.purpose, tab: "Hearings" },
    { icon: AlertTriangle, k: "Next Deadline", v: snap.nextDeadline ? formatDate(snap.nextDeadline) : "—", s: "Limitation", tab: "Snapshot" },
    { icon: ScrollText, k: "Latest Order", v: snap.latestOrder ? formatDate(snap.latestOrder.date) : "None", s: snap.latestOrder?.title || snap.latestOrder?.type, tab: "Orders & Compliance" },
    { icon: ListTodo, k: "Open Tasks", v: snap.openTasks, s: snap.overdueTasks ? `${snap.overdueTasks} overdue` : "on track", tab: "Tasks" },
    { icon: PenLine, k: "Drafts", v: snap.drafts.total, s: `${snap.drafts.reviewPending} awaiting review`, tab: "Drafts" },
    { icon: GitBranch, k: "ProofGraph Gaps", v: snap.proofGaps, s: `${snap.openConflicts} fact conflict(s)`, tab: "ProofGraph" },
    { icon: FileText, k: "Documents", v: snap.documents, s: `${snap.openRequests} request(s) open`, tab: "Docs & Exhibits" },
  ];
  return (
    <div className="space-y-4" data-testid="snapshot-command-centre">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((c) => (
          <button key={c.k} onClick={() => onOpen(c.tab)} className="rounded-xl border border-line bg-card p-3 text-left transition-colors hover:border-copper/40" data-testid={`snapshot-card-${c.k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-1.5 text-xs text-ink-2"><c.icon className="h-3.5 w-3.5 text-copper" />{c.k}</div>
            <div className="mt-1 truncate font-display text-xl font-semibold text-ink">{c.v}</div>
            <div className="truncate text-[11px] text-ink-muted">{c.s || "—"}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel className="p-4 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold text-ink">Next Actions</h3>
          {snap.nextActions.length ? (
            <ul className="mt-2 space-y-1.5" data-testid="next-actions">
              {snap.nextActions.map((a, i) => <li key={`${a.label}-${i}`}><button onClick={() => onOpen(a.tab)} className="flex w-full items-center justify-between rounded-lg border border-line px-3 py-2 text-sm text-ink hover:border-copper/40" data-testid={`next-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>{a.label}<ArrowRight className="h-4 w-4 text-copper" /></button></li>)}
            </ul>
          ) : <p className="mt-2 text-sm text-ink-2">Nothing requires immediate action.</p>}
          {children}
        </Panel>
        <Panel className="p-4">
          <h3 className="font-display text-base font-semibold text-ink">Recent Activity</h3>
          <ul className="mt-2 space-y-1.5 text-xs text-ink-2" data-testid="recent-activity">
            {snap.recentActivity.map((a, i) => <li key={a.id || i}>• {a.action.replaceAll(".", " · ").replaceAll("_", " ")} <span className="text-ink-muted">{fromNow(a.at)}</span></li>)}
            {!snap.recentActivity.length && <li>No activity recorded yet.</li>}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
